/**
 * ספרי מחירים — the price catalogue's PARENT screen. 08 §11 entity #8, migration 0015.
 *
 * A list of books, and the one act that creates another. One book's prices live at that book's own
 * address (`PlatformPriceBookScreen`), because 09 §1.3 makes a noun a screen and 09 §3.4 puts every
 * selector in the shell — a picker here would be a module owning a dropdown, which is a second
 * place a context gets chosen.
 *
 * ── WHY A BOOK ARRIVES AS A DRAFT ───────────────────────────────────────────────────────────────
 *
 * Creating a book and publishing one are different acts. A book that arrived published would be a
 * live price list produced by a single POST, before anybody had put a price in it.
 */

import { useCallback, useState } from 'react';
import type { FormEvent } from 'react';

import { AppShell } from '../shell/AppShell';
import { useWorkContext } from '../shell/ContextProvider';
import { DataTable } from '../components/DataTable';
import { ServerRead } from '../components/ServerRead';
import { StateBlock } from '../components/StateBlock';
import { StatusChip } from '../components/StatusChip';
import { SurfaceNotice } from '../components/SurfaceNotice';
import { ActionFailureNotice } from '../components/ActionFailureNotice';
import type { Column } from '../components/DataTable';
import type { PriceBookSummary } from '../api/contract';
import type { ActionFailure } from '../api/actionOutcome';
import { classifyActionError } from '../api/actionOutcome';
import { callOperation } from '../api/operations';
import { useServerData } from '../api/useServerData';
import { routes } from '../routes';

const TITLE_HE = 'ספרי מחירים';

export function PlatformPriceBooksScreen(): JSX.Element {
  const ctx = useWorkContext();

  const readBooks = useCallback(() => callOperation('platform.price_book.read', {}), []);
  const booksRead = useServerData(readBooks);

  const [failure, setFailure] = useState<ActionFailure | null>(null);
  const [sending, setSending] = useState(false);
  /* The code is derived from the English name and stays editable -- the catalogue screen carries
     the full reasoning beside its own `codeFrom`. */
  const [code, setCode] = useState('');
  const [codeTouched, setCodeTouched] = useState(false);

  /** Upper case, runs of non-Latin-alphanumerics become one `_`, ends trimmed, 32 at most. */
  function codeFrom(nameEn: string): string {
    return nameEn
      .toUpperCase()
      .replace(/[^A-Z0-9]+/gu, '_')
      .replace(/^_+|_+$/gu, '')
      .slice(0, 32);
  }

  if (ctx.surface !== 'PLATFORM') {
    return <SurfaceNotice titleHe={TITLE_HE} requires="PLATFORM" actual={ctx.surface} />;
  }

  function createBook(e: FormEvent): void {
    e.preventDefault();
    const form = new FormData(e.target as HTMLFormElement);
    setSending(true);
    setFailure(null);
    void callOperation('platform.price_book.create', {
      body: {
        code: String(form.get('code') ?? ''),
        displayNameHe: String(form.get('displayNameHe') ?? ''),
        displayNameEn: String(form.get('displayNameEn') ?? ''),
        currencyCode: String(form.get('currencyCode') ?? ''),
      },
    })
      .then(
        () => booksRead.reload(),
        (err: unknown) => setFailure(classifyActionError(err)),
      )
      .finally(() => setSending(false));
  }

  const columns: readonly Column<PriceBookSummary>[] = [
    {
      key: 'name',
      headerHe: 'ספר המחירים',
      render: (b) => (
        <span className="company-cell">
          <span className="company-cell__name">{b.displayNameHe}</span>
          <span className="company-cell__id" dir="ltr">
            {b.code}
          </span>
        </span>
      ),
    },
    { key: 'currency', headerHe: 'מטבע', render: (b) => <span dir="ltr">{b.currencyCode}</span> },
    {
      key: 'state',
      headerHe: 'מצב',
      render: (b) =>
        b.isPublished ? (
          <StatusChip labelHe="מחירון רשמי" tone="ok" />
        ) : b.status === 'draft' ? (
          <StatusChip labelHe="טיוטה" tone="warn" />
        ) : (
          <StatusChip labelHe="פעיל" tone="muted" />
        ),
    },
    {
      key: 'items',
      headerHe: 'שורות מחיר',
      numeric: true,
      render: (b) => <span dir="ltr">{b.itemCount}</span>,
    },
  ];

  return (
    <AppShell
      titleHe={TITLE_HE}
      backToParent={{ labelHe: 'קונסולת הפלטפורמה', href: routes.platformConsole }}
    >
      {failure === null ? null : <ActionFailureNotice failure={failure} />}

      <ServerRead result={booksRead.result} whatHe="את ספרי המחירים" skeletonRows={4}>
        {(data) =>
          data.priceBooks.length === 0 ? (
            /*
             * A LEGITIMATE EMPTY. The read succeeded and there are no books, which is the correct
             * state of a platform before its first price list is written.
             */
            <StateBlock
              titleHe="עדיין אין ספר מחירים"
              bodyHe="הקריאה לשרת הצליחה ואין ספרי מחירים. זהו המצב הצפוי לפני שנקבע מחירון ראשון."
              tone="empty"
            />
          ) : (
            <DataTable<PriceBookSummary>
              captionHe="ספרי מחירים"
              columns={columns}
              rows={data.priceBooks}
              rowKey={(b) => b.priceBookUid}
              childHref={(b) => routes.platformPriceBook(b.priceBookUid)}
              filterSummaryHe={`מציג ${data.priceBooks.length} ספרי מחירים`}
              emptyStateHe="אין ספרי מחירים."
            />
          )
        }
      </ServerRead>

      {/*
        ── IT WAS `signin__form`. **Owner, section 2, 2026-11-01.** ────────────────────────────

            *"הפריסה עדיין ארוכה ומסורבלת ... טפסים קומפקטיים בשורות או בקוביות סמוכות, עם ניצול
            רוחב המסך."*

        `signin__form` is the SIGN-IN CARD's layout: a narrow centred column, which is right for
        two fields on an empty page and wrong for four fields on a console screen. Four stacked
        full-width rows inside a 420px column is the long clumsy form the Owner is describing, and
        it was a class name borrowed from another screen rather than a decision.

        `.form` with a `--wide` group is this product's own form layout and already flows its
        fields into `repeat(auto-fit, minmax(15rem, 1fr))`, collapsing to one column under 48rem.
        Nothing new was written for this; the screen stopped using the wrong one.
      */}
      <form className="form" onSubmit={createBook}>
        <div className="form__group form__group--wide">
          <h3 className="form__group-title">ספר מחירים חדש</h3>
          <p className="tile__lead">
            ספר חדש נוצר כטיוטה. פרסום כמחירון הרשמי הוא פעולה נפרדת, במסך הספר עצמו.
          </p>
          <label className="field">
            <span className="field__label">שם בעברית</span>
            <input className="field__input" name="displayNameHe" required />
          </label>
          <label className="field">
            <span className="field__label">שם באנגלית</span>
            <input
              className="field__input"
              name="displayNameEn"
              dir="ltr"
              onChange={(e) => {
                if (!codeTouched) setCode(codeFrom(e.currentTarget.value));
              }}
              required
            />
          </label>
          {/* The same derivation as the catalogue's two codes, and the same reason: the Owner
              should not have to invent a Latin identifier for a Hebrew name. Still editable. */}
          <label className="field">
            <span className="field__label">קוד — נוצר אוטומטית מהשם באנגלית</span>
            <input
              className="field__input"
              name="code"
              dir="ltr"
              value={code}
              onChange={(e) => {
                setCodeTouched(true);
                setCode(e.currentTarget.value);
              }}
              required
            />
          </label>
          <label className="field">
            <span className="field__label">מטבע</span>
            <input
              className="field__input"
              name="currencyCode"
              dir="ltr"
              maxLength={3}
              defaultValue="ILS"
              required
            />
          </label>
          <div className="actions-row">
            <button type="submit" className="btn btn--primary" disabled={sending}>
              יצירת ספר מחירים
            </button>
          </div>
        </div>
      </form>
    </AppShell>
  );
}
